import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Container } from "react-bootstrap";

import Wave from "./Wave";

const CARD_HEIGHT = 344;
const CARD_GAP = 32;
const SLIDE_OFFSET = CARD_HEIGHT + CARD_GAP;
const WHEEL_THRESHOLD = 180;
const SCROLL_THRESHOLD = 30;
const SCROLL_LOCK_DELAY = 900;

const STEP_REGEX = /Events_Step$/;
const STORY_REGEX = /Events_Story$/;
const SLIDE_REGEX = /Events_Step$|Events_Story$/;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const Timeline = ({ events, title, text }) => {
    const slides = useMemo(
        () => events.filter((event) => SLIDE_REGEX.test(event.fieldGroupName)),
        [events]
    );

    const [activeIndex, setActiveIndex] = useState(0);
    const viewportRef = useRef(null);
    const scrollLockRef = useRef(null);
    const wheelDeltaRef = useRef(0);
    const wheelDirectionRef = useRef(null);
    const touchStartYRef = useRef(null);
    const touchSlideHandledRef = useRef(false);

    const maxIndex = Math.max(slides.length - 1, 0);

    const goToSlide = useCallback(
        (index) => {
            setActiveIndex(clamp(index, 0, maxIndex));
        },
        [maxIndex]
    );

    const canMoveByDirection = useCallback(
        (direction) =>
            direction > 0 ? activeIndex < maxIndex : activeIndex > 0,
        [activeIndex, maxIndex]
    );

    const goByDirection = useCallback(
        (direction) => {
            setActiveIndex((prev) => clamp(prev + direction, 0, maxIndex));
        },
        [maxIndex]
    );

    useEffect(() => {
        const viewport = viewportRef.current;

        if (!viewport || slides.length <= 1) return undefined;

        const handleWheel = (event) => {
            const direction = event.deltaY > 0 ? 1 : -1;

            if (!canMoveByDirection(direction)) {
                wheelDeltaRef.current = 0;
                wheelDirectionRef.current = null;
                return;
            }

            event.preventDefault();

            if (scrollLockRef.current) {
                return;
            }

            if (wheelDirectionRef.current !== direction) {
                wheelDeltaRef.current = 0;
                wheelDirectionRef.current = direction;
            }

            wheelDeltaRef.current += Math.abs(event.deltaY);

            if (wheelDeltaRef.current < WHEEL_THRESHOLD) {
                return;
            }

            wheelDeltaRef.current = 0;
            goByDirection(direction);

            scrollLockRef.current = window.setTimeout(() => {
                scrollLockRef.current = null;
            }, SCROLL_LOCK_DELAY);
        };

        const handleTouchStart = (event) => {
            touchStartYRef.current = event.touches[0].clientY;
            touchSlideHandledRef.current = false;
        };

        const handleTouchMove = (event) => {
            if (touchStartYRef.current === null) return;

            const diff = touchStartYRef.current - event.touches[0].clientY;

            if (Math.abs(diff) < SCROLL_THRESHOLD) return;

            const direction = diff > 0 ? 1 : -1;

            if (!canMoveByDirection(direction)) {
                return;
            }

            event.preventDefault();

            if (touchSlideHandledRef.current) {
                return;
            }

            touchSlideHandledRef.current = true;
            goByDirection(direction);
        };

        const handleTouchEnd = () => {
            touchStartYRef.current = null;
            touchSlideHandledRef.current = false;
        };

        viewport.addEventListener("wheel", handleWheel, { passive: false });
        viewport.addEventListener("touchstart", handleTouchStart, {
            passive: true,
        });
        viewport.addEventListener("touchmove", handleTouchMove, {
            passive: false,
        });
        viewport.addEventListener("touchend", handleTouchEnd);

        return () => {
            viewport.removeEventListener("wheel", handleWheel);
            viewport.removeEventListener("touchstart", handleTouchStart);
            viewport.removeEventListener("touchmove", handleTouchMove);
            viewport.removeEventListener("touchend", handleTouchEnd);

            if (scrollLockRef.current) {
                window.clearTimeout(scrollLockRef.current);
                scrollLockRef.current = null;
            }

            wheelDeltaRef.current = 0;
            wheelDirectionRef.current = null;
        };
    }, [canMoveByDirection, goByDirection, slides.length]);

    const getProgressPercent = (index) => {
        if (slides.length <= 1) return 50;

        return (index / (slides.length - 1)) * 100;
    };

    const progressHeight =
        slides.length <= 1 ? 0 : getProgressPercent(activeIndex);

    return (
        <div className="timeline timeline--vertical-slider container">
            <Container className="timeline__container">
                <div className="timeline__intro">
                    {title && (
                        <h3
                            className="timeline__intro-title"
                            dangerouslySetInnerHTML={{ __html: title }}
                        />
                    )}

                    {text && (
                        <div
                            className="timeline__intro-text"
                            dangerouslySetInnerHTML={{ __html: text }}
                        />
                    )}
                </div>

                <div className="timeline__vertical-wrapper">
                    <div className="timeline__vertical-line">
                        <span
                            className="timeline__progress"
                            style={{
                                top: 0,
                                height: `${progressHeight}%`,
                            }}
                        />

                        {slides.map((_, index) => (
                            <button
                                key={index}
                                type="button"
                                className={`timeline__dot ${
                                    index <= activeIndex ? "-active" : ""
                                }`}
                                style={{
                                    top: `${getProgressPercent(index)}%`,
                                }}
                                onMouseDown={(event) => event.stopPropagation()}
                                onMouseUp={(event) => event.stopPropagation()}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    goToSlide(index);
                                }}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>

                    <div ref={viewportRef} className="timeline__viewport">
                        <div
                            className="timeline__slides"
                            style={{
                                transform: `translateY(-${
                                    activeIndex * SLIDE_OFFSET
                                }px)`,
                            }}
                        >
                            {slides.map((event, index) => (
                                <TimelineEvent key={index} {...event} />
                            ))}
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
};

const TimelineEvent = ({ fieldGroupName, ...eventData }) => {
    if (STEP_REGEX.test(fieldGroupName)) {
        return <TimelineStep {...eventData} />;
    }

    if (STORY_REGEX.test(fieldGroupName)) {
        return <TimelineStory {...eventData} />;
    }

    return null;
};

const TimelineStep = ({ title, text }) => (
    <TimelineCard title={title} text={text} titleTag="p" />
);

const TimelineStory = ({ year, text }) => (
    <TimelineCard title={year} text={text} titleTag="h3" story />
);

const TimelineCard = ({ title, text, titleTag: TitleTag, story = false }) => (
    <div className={`timeline__step ${story ? "-story" : ""}`}>
        <TitleTag
            className={story ? "timeline__year" : "timeline__title"}
            dangerouslySetInnerHTML={{ __html: title }}
        />

        <p
            className="timeline__text"
            dangerouslySetInnerHTML={{ __html: text }}
        />

        <div className="timeline__wave-container wave-container">
            <Wave className="timeline__wave -deepblue250" variant={2} />
        </div>
    </div>
);

export default Timeline;
